import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileSpreadsheet, Upload as UploadIcon, Download, CheckCircle, AlertTriangle, Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

const FIELD_LABELS = {
  set_name: "Set Name",
  name: "Card Name",
  condition: "Condition",
  card_number: "Card Number",
  quantity: "Quantity",
  variant: "Variant",
  language: "Language",
  grade: "Grade",
  purchase_price: "Purchase Price",
  notes: "Notes",
};

export default function Import() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [duplicateStrategy, setDuplicateStrategy] = useState("add_quantity");
  const [committing, setCommitting] = useState(false);
  const [job, setJob] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!job || job.status === "completed" || job.status === "failed") return;
    const t = setInterval(async () => {
      try {
        const res = await api.get(`/import/jobs/${job.id}`);
        setJob(res.data);
        if (res.data.status === "completed" || res.data.status === "failed") {
          checkAuth();
        }
      } catch {
        clearInterval(t);
      }
    }, 1500);
    return () => clearInterval(t);
  }, [job, checkAuth]);

  const chooseFile = (f) => {
    if (!f) return;
    if (!/\.(csv|xlsx|xls)$/i.test(f.name)) {
      toast.error("Please upload a .csv, .xlsx or .xls file");
      return;
    }
    setFile(f);
    setPreview(null);
    setJob(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read the file");
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async () => {
    if (!file || !preview) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("duplicate_strategy", duplicateStrategy);
      const res = await api.post("/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob({ id: res.data.job_id, status: "processing", total: res.data.total, processed: 0, failed_rows: [], created_ids: [] });
      toast.success(`Started importing ${res.data.total} cards`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the import");
    } finally {
      setCommitting(false);
    }
  };

  const progressPct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;
  const credits = user?.unlimited_credits ? Infinity : (user?.credits_balance ?? 0);
  const canAfford = credits === Infinity || (preview && preview.credit_cost <= credits);

  return (
    <div className="min-h-screen bg-[#05060f] text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 data-testid="import-title" className="font-display text-4xl sm:text-5xl font-bold mb-2 flex items-center gap-3">
          <FileSpreadsheet className="w-8 h-8 text-[#A78BFA]" /> Bulk import
        </h1>
        <p className="text-zinc-400 text-sm mb-8">
          Bring a CSV or Excel file of cards straight into your binder. No scan needed — we look up the official card image + market price from the set and number.
        </p>

        <div className="border border-white/10 bg-[#111113] rounded-2xl p-6 mb-6">
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Download className="w-4 h-4 text-[#A78BFA]" /> Column format
          </h2>
          <p className="text-xs text-zinc-500 mb-4">
            Required: <span className="text-zinc-300">Set Name</span> and <span className="text-zinc-300">Card Name</span>. Everything else is optional but sharpens identification and pricing.
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-zinc-400 mb-5">
            <div><span className="text-white">A. Set Name</span> — required</div>
            <div><span className="text-white">F. Variant</span> — Holo, Reverse, 1st Edition</div>
            <div><span className="text-white">B. Card Name</span> — required</div>
            <div><span className="text-white">G. Language</span> — defaults to English</div>
            <div><span className="text-white">C. Condition</span> — NM/LP/MP/HP/DMG</div>
            <div><span className="text-white">H. Grade</span> — e.g. PSA 10, BGS 9.5</div>
            <div><span className="text-white">D. Card Number</span> — like 4/102</div>
            <div><span className="text-white">I. Purchase Price</span> — for Sales Ledger</div>
            <div><span className="text-white">E. Quantity</span> — defaults to 1</div>
            <div><span className="text-white">J. Notes</span> — free text</div>
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/import/template.csv`}
            data-testid="import-template-download"
            className="inline-flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white transition-colors font-medium"
          >
            <Download className="w-3.5 h-3.5" /> Download the CSV template
          </a>
        </div>

        <div
          data-testid="import-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); chooseFile(e.dataTransfer.files?.[0]); }}
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-white/10 hover:border-[#A78BFA]/40 hover:bg-white/[0.02] rounded-2xl p-10 text-center cursor-pointer transition-colors"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            data-testid="import-file-input"
            className="hidden"
            onChange={(e) => chooseFile(e.target.files?.[0])}
          />
          <UploadIcon className="w-8 h-8 text-[#A78BFA] mx-auto mb-3" />
          <p data-testid="import-filename" className="font-medium mb-1">
            {file ? file.name : "Drop a CSV or Excel file here, or click to pick"}
          </p>
          <p className="text-xs text-zinc-500">Up to 5 000 rows · 5 MB max</p>
        </div>

        {file && !preview && !job && (
          <Button
            data-testid="import-preview-button"
            onClick={runPreview}
            disabled={previewing}
            className="w-full mt-4 bg-[#A78BFA] hover:bg-[#8B6FE8] text-black font-semibold rounded-full"
          >
            {previewing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Reading file…</> : <>Preview rows</>}
          </Button>
        )}

        {preview && !job && (
          <div data-testid="import-preview" className="mt-6 space-y-4">
            <div className="border border-white/10 bg-[#111113] rounded-2xl p-5">
              <p className="text-sm font-medium mb-2">
                {preview.total_rows} rows detected · costs <span className="text-[#A78BFA] font-mono-plex">{preview.credit_cost}</span> credits
                {user?.unlimited_credits ? " (unlimited plan)" : ` · you have ${Math.floor(credits)}`}
              </p>
              <p className="text-xs text-zinc-500 mb-3">
                Column mapping:{" "}
                {Object.keys(preview.column_mapping).length
                  ? Object.entries(preview.column_mapping).map(([k, v]) =>
                      <span key={k} className="inline-block mr-2"><span className="text-white">{FIELD_LABELS[k] || k}</span> = col {v + 1}</span>)
                  : <span className="text-amber-300">No column mapping — the file might be missing headers.</span>}
              </p>
              <div className="text-xs">
                <div className="mb-2 text-zinc-500">First 5 rows:</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-zinc-500 text-[10px] uppercase tracking-wide">
                        <th className="pr-3 pb-1">Set</th>
                        <th className="pr-3 pb-1">Name</th>
                        <th className="pr-3 pb-1">Cond.</th>
                        <th className="pr-3 pb-1">#</th>
                        <th className="pr-3 pb-1">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.slice(0, 5).map((row, i) =>
                        <tr key={i} className="border-t border-white/5">
                          <td className="pr-3 py-1 text-zinc-300">{row.set_name || <span className="text-amber-300">—</span>}</td>
                          <td className="pr-3 py-1 text-zinc-300">{row.name || <span className="text-amber-300">—</span>}</td>
                          <td className="pr-3 py-1 text-zinc-500">{row.is_graded ? `${row.grading_company} ${row.grade}` : row.condition}</td>
                          <td className="pr-3 py-1 text-zinc-500">{row.card_number || "—"}</td>
                          <td className="pr-3 py-1 text-zinc-500">{row.quantity || 1}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="border border-white/10 bg-[#111113] rounded-2xl p-5 space-y-3">
              <p className="text-sm font-medium">If a card already exists in your binder…</p>
              {[
                { v: "add_quantity", label: "Add to the existing card's quantity", desc: "Recommended — merges duplicates cleanly." },
                { v: "skip", label: "Skip it", desc: "Only import cards you don't already have." },
                { v: "create_new", label: "Create a separate entry", desc: "Keeps identical cards as separate rows." },
              ].map((opt) => (
                <label key={opt.v} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border ${duplicateStrategy === opt.v ? "border-[#A78BFA]/50 bg-[#A78BFA]/5" : "border-white/5 hover:bg-white/[0.02]"}`}>
                  <input
                    type="radio"
                    name="dupe"
                    value={opt.v}
                    checked={duplicateStrategy === opt.v}
                    onChange={() => setDuplicateStrategy(opt.v)}
                    data-testid={`import-dupe-${opt.v}`}
                    className="mt-1 accent-[#A78BFA]"
                  />
                  <div>
                    <div className="text-sm text-white">{opt.label}</div>
                    <div className="text-xs text-zinc-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            {!canAfford && (
              <div className="border border-amber-400/30 bg-amber-500/5 rounded-xl p-4 text-sm text-amber-300 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  Not enough credits for this import.
                  <Link to="/billing" className="ml-1 underline">Top up here</Link>.
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                data-testid="import-commit-button"
                onClick={runImport}
                disabled={committing || !canAfford}
                className="flex-1 bg-[#A78BFA] hover:bg-[#8B6FE8] text-black font-semibold rounded-full"
              >
                {committing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Starting…</> : <>Import {preview.total_rows} cards</>}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setPreview(null); setFile(null); }}
                className="border-white/10 hover:bg-white/5 rounded-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {job && (
          <div data-testid="import-progress" className="mt-6 border border-white/10 bg-[#111113] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium">
                {job.status === "completed" ? (
                  <span className="text-emerald-300 flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> Import complete</span>
                ) : job.status === "failed" ? (
                  <span className="text-red-400 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Import failed</span>
                ) : (
                  <span className="flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin text-[#A78BFA]" /> Importing your cards…</span>
                )}
              </p>
              <span data-testid="import-progress-percent" className="font-mono-plex text-[#A78BFA]">{job.processed}/{job.total}</span>
            </div>
            <Progress value={progressPct} className="h-2" />
            {job.failed_rows?.length > 0 && (
              <div className="mt-4 text-xs">
                <p className="text-amber-300 mb-2">{job.failed_rows.length} row{job.failed_rows.length !== 1 ? "s" : ""} couldn&apos;t be imported:</p>
                <div className="max-h-40 overflow-y-auto space-y-0.5 text-zinc-500 font-mono-plex">
                  {job.failed_rows.slice(0, 30).map((f, i) =>
                    <div key={i}>Row {f.row}: {f.error}</div>)}
                  {job.failed_rows.length > 30 && <div>…and {job.failed_rows.length - 30} more</div>}
                </div>
              </div>
            )}
            {job.status === "completed" && (
              <Button
                data-testid="import-view-binder-button"
                onClick={() => navigate("/binder")}
                className="mt-5 w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full"
              >
                View your binder
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
